import { treaty } from "@elysiajs/eden";
import type { App } from "@server/app";
import { ApiError, handleUnauthorized } from "@/lib/queryClient";

declare global {
  interface Window {
    /** Session token injected by the mobile app's card webview (PRD 12 D13). */
    __BDAY_APP_TOKEN__?: string;
    /** Set by the app's browser hand-off (BDAY-303): send ONLY the token, never cookies —
     *  a browser holding another member's session cookie must not win over it. */
    __BDAY_TOKEN_ONLY__?: boolean;
  }
}

let _seq = 0;
function nextSeq() {
  _seq = (_seq % 999) + 1;
  return String(_seq).padStart(3, "0");
}

function formatBytes(n: number) {
  if (n < 1000) return `${n} bytes`;
  return `${(n / 1024).toFixed(1)} KB`;
}

/**
 * Request/response logging is DEV-ONLY: `Sentry.replayIntegration()` (main.tsx)
 * ships console output off the device, so logging login/password bodies in prod
 * would leak them. Even in dev, credential fields are masked below — a shared
 * screen or console dump shouldn't carry a real password or bearer token.
 */
const DEV = import.meta.env.DEV;

const REDACTED = "[redacted]";

/**
 * Field/query names whose values are credentials — `slt`, `dt`, and `reply_token`
 * are capability tokens: holding one IS the authorisation.
 */
const REDACT_KEYS = new Set([
  "password",
  "current_password",
  "new_password",
  "confirm_password",
  "reset_token",
  "session_token",
  "reply_token",
  "token",
  "slt",
  "dt",
]);

function isPlainObject(v: unknown): v is Record<string, unknown> {
  if (v === null || typeof v !== "object") return false;
  const proto = Object.getPrototypeOf(v) as object | null;
  return proto === Object.prototype || proto === null;
}

/** Deep copy with every credential-named field masked. Depth-capped. */
function redact(value: unknown, depth = 0): unknown {
  if (depth > 8) return value;
  if (Array.isArray(value)) return value.map((v) => redact(v, depth + 1));
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = REDACT_KEYS.has(k.toLowerCase()) ? REDACTED : redact(v, depth + 1);
    }
    return out;
  }
  return value;
}

/** A request body, safe to print: parsed + redacted, or a type tag we can't see into. */
function redactBody(body: BodyInit | null | undefined): unknown {
  if (body == null) return undefined;
  if (typeof body === "string") {
    try {
      return redact(JSON.parse(body));
    } catch {
      // Form-encoded or opaque — may itself be `password=...`, so don't print it.
      return "[body]";
    }
  }
  if (body instanceof FormData) return "[FormData]";
  if (body instanceof URLSearchParams) return "[URLSearchParams]";
  if (body instanceof Blob) return "[Blob]";
  return "[body]";
}

/** Capability tokens that ride in the PATH rather than the query string. */
const PATH_TOKEN_RE = /\/(support\/ticket|support\/tickets\/by-token)\/[^/?#]+/g;

/** A URL safe to print: capability query params and path segments masked. */
function redactUrl(raw: string): string {
  let u: URL;
  try {
    u = new URL(raw, window.location.origin);
  } catch {
    return "[url]";
  }
  // Snapshot the keys: `set()` collapses repeated params, i.e. mutates what we iterate.
  const keys = Array.from(u.searchParams.keys());
  for (const key of keys) {
    if (REDACT_KEYS.has(key.toLowerCase())) u.searchParams.set(key, REDACTED);
  }
  u.pathname = u.pathname.replace(PATH_TOKEN_RE, (_m, prefix: string) => `/${prefix}/${REDACTED}`);
  return u.toString();
}

const loggingFetch = async (input: URL | RequestInfo, init?: RequestInit): Promise<Response> => {
  const url =
    typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
  const method = init?.method ?? "GET";
  const seq = DEV ? nextSeq() : "";
  const safeUrl = DEV ? redactUrl(url) : "";

  if (DEV) {
    const bodyObj = redactBody(init?.body);
    const isMutating = method === "PUT" || method === "POST" || method === "DELETE";
    if (isMutating) {
      console.log(
        `%c-> ${seq} ${method} ${safeUrl}`,
        "color: #facc15",
        bodyObj !== undefined ? bodyObj : "",
      );
    } else {
      console.log(`-> ${seq} ${method} ${safeUrl}`, bodyObj !== undefined ? bodyObj : "");
    }
  }

  // Card DOM surface (PRD 12 D13): when this page runs inside the mobile app's
  // webview, it has no session cookie. The app injects the member's session token
  // as `window.__BDAY_APP_TOKEN__` before load; forward it as a Bearer, which the
  // server accepts identically to the cookie (server/middleware/auth.ts). The
  // global only exists inside our own webview — a real browser never sets it.
  // Same-origin guard: this fetcher only ever targets our origin today, but if it
  // is ever reused for a foreign URL the session token must not ride along.
  const appToken =
    new URL(url, window.location.origin).origin === window.location.origin
      ? window.__BDAY_APP_TOKEN__
      : undefined;
  let authedInit = init;
  if (appToken) {
    const headers = new Headers(init?.headers);
    if (!headers.has("Authorization")) headers.set("Authorization", `Bearer ${appToken}`);
    authedInit = { ...init, headers };
  }

  const start = performance.now();
  const res = await fetch(input, {
    ...authedInit,
    credentials:
      appToken && window.__BDAY_TOKEN_ONLY__ ? "omit" : (authedInit?.credentials ?? "include"),
  });
  const ms = Math.round(performance.now() - start);

  // Only the auth guard's own rejection means the session is gone — other 401s
  // carry their own detail and must not sign the member out.
  if (res.status === 401) {
    void res
      .clone()
      .json()
      .then((body: unknown) => {
        const detail = (body as { detail?: unknown } | null)?.detail;
        if (detail === "Not authenticated") handleUnauthorized();
      })
      .catch(() => {});
  }

  if (DEV) {
    // Reading the clone is only worth the bytes when we're going to print them.
    const cloned = res.clone();
    let resObj: unknown;
    let size = 0;
    try {
      const text = await cloned.text();
      size = new Blob([text]).size;
      resObj = redact(JSON.parse(text));
    } catch {
      /* suppress */
    }
    const logStyle = res.ok ? "color: #4ade80" : "color: #f87171; font-weight: bold";
    console.log(
      `%c<- ${seq} ${method} ${safeUrl} ${res.status} ${ms}ms ${formatBytes(size)}`,
      logStyle,
      resObj !== undefined ? resObj : "",
    );
  }

  return res;
};
// Eden's fetcher type expects preconnect; native fetch doesn't have it.
const fetcherWithPreconnect = Object.assign(loggingFetch, { preconnect: (_url: string) => {} });

// parseDate: false keeps ISO-8601 timestamp strings as strings on the wire.
// Otherwise Eden silently converts them to Date objects, which diverges from
// the server's typed `t.String()` and breaks anything that does `typeof === "string"`.
const edenOpts = {
  fetcher: fetcherWithPreconnect as typeof fetch,
  fetch: { credentials: "include" as RequestCredentials },
  parseDate: false as const,
};

export const api = treaty<App>(window.location.origin, edenOpts);

/** Thin wrapper: awaits Eden call, throws on error, returns data. Use for simple api.api.X.Y.get/post() calls. */
export async function edenFetch<T>(
  call: Promise<{ data: T; error: unknown }>,
): Promise<NonNullable<T>> {
  const { data, error } = await call;
  if (error) {
    const e = error as { status?: number; value?: unknown };
    const status = e.status ?? 500;
    const val = e.value;
    // Not our JSON (an edge/gateway error page, an empty body): say what came back
    // rather than a generic "went wrong" nobody can act on (BDAY-393).
    let detail = `The server didn't answer properly (HTTP ${status})`;
    if (val != null && typeof val === "object" && "detail" in val) {
      const d = (val as { detail?: unknown }).detail;
      if (typeof d === "string") detail = d;
    } else if (typeof val === "string" && !val.trim().startsWith("<")) {
      detail = val;
    }
    throw new ApiError(detail, status);
  }
  return data as NonNullable<T>;
}
